import { CacheProvider } from '@emotion/core';
import { cache } from 'emotion';
import App from 'next/app';
import Router from 'next/router';
import React from 'react';

import ErrorHandler from 'site-react/core/ErrorHandler';
import { FeatureFlagsProvider } from 'site-react/core/FeatureFlags';
import { PersonaProvider } from 'site-react/data/core/PersonaContext';
import { ShortlistProvider } from 'site-react/data/core/ShortlistContext';
import { UserProvider } from 'site-react/data/core/UserContext';

class MyApp extends App {
  /**
   * Handle history.scrollRestoration manually when client-side routing
   *
   * Required because Next doesn’t handle automatic scrollRestoration correctly.
   * See: https://github.com/zeit/next.js/issues/3303. This thread highlights
   * various issues, and suggests potential causes and solutions. The most
   * deterministic solution is to switch to manual scrollRestoration when
   * client-side routing. This way the browser won’t try and interject with
   * unpredictable automatic behaviour, and edge-case can be addressed directly
   * in code.
   *
   * To ensure that history navigation is performant, you may also need to cache
   * data from getInitialProps. See search page for an example implementation.
   */
  componentDidMount() {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';

      const cachedScrollPositions = [];
      let shouldScrollRestore;

      Router.events.on('routeChangeStart', () => {
        document.documentElement.classList.add('html--autoScroll');

        if (!shouldScrollRestore) {
          cachedScrollPositions.push([window.scrollX, window.scrollY]);
        }
      });

      Router.events.on('routeChangeComplete', () => {
        if (shouldScrollRestore) {
          const { x, y } = shouldScrollRestore;

          window.scrollTo(x, y);
          shouldScrollRestore = false;
        }

        document.documentElement.classList.remove('html--autoScroll');
      });

      Router.beforePopState(() => {
        if (cachedScrollPositions.length) {
          const [x, y] = cachedScrollPositions.pop();

          shouldScrollRestore = { x, y };
        }
        return true;
      });
    }
  }

  componentWillUnmount() {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
  }

  render() {
    const { Component, pageProps } = this.props;
    return (
      <CacheProvider value={cache}>
        <ErrorHandler>
          <FeatureFlagsProvider>
            <UserProvider>
              <PersonaProvider>
                <ShortlistProvider>
                  <Component {...pageProps} />
                </ShortlistProvider>
              </PersonaProvider>
            </UserProvider>
          </FeatureFlagsProvider>
        </ErrorHandler>
      </CacheProvider>
    );
  }
}

export default MyApp;
